import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import tripDetailsConfig from '../config/tripDetails.json';

// Admin phone numbers
const ADMIN_PHONE_NUMBERS = [
  '+15407939110',
];

function TripDetails() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [lodgingPhoto, setLodgingPhoto] = useState(null);
  const [photoLoading, setPhotoLoading] = useState(true);
  const [photoError, setPhotoError] = useState(false);
  const [copySuccess, setCopySuccess] = useState({});
  const [loading, setLoading] = useState(true);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [expandedDays, setExpandedDays] = useState({});
  const [restaurantPhotos, setRestaurantPhotos] = useState({});
  const [restaurantPhotoLoading, setRestaurantPhotoLoading] = useState({});
  const [restaurantPhotoError, setRestaurantPhotoError] = useState({});
  const fetchedRestaurantUrls = useRef(new Set());

  useEffect(() => {
    const isDevMode = localStorage.getItem('devMode') === 'true';
    if (!currentUser && !isDevMode) {
      navigate('/');
      return;
    }

    const checkConfirmedStatus = async () => {
      try {
        const isAdmin = currentUser && ADMIN_PHONE_NUMBERS.includes(currentUser.phoneNumber);
        
        // Admins can always access
        if (isAdmin) {
          setIsConfirmed(true);
          setLoading(false);
          return;
        }

        // Check Firestore for user's response
        if (db && currentUser?.uid) {
          const responsesRef = collection(db, 'responses');
          const q = query(responsesRef, where('userId', '==', currentUser.uid));
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            const userResponse = querySnapshot.docs[0].data();
            const confirmed = userResponse.status === 'confirmed';
            setIsConfirmed(confirmed);
            
            if (!confirmed) {
              // Redirect if not confirmed
              navigate('/home', { 
                state: { 
                  message: 'You must be confirmed for the trip to view trip details. Please contact an admin.' 
                } 
              });
              return;
            }
          } else {
            // No response found, check localStorage
            const localResponses = JSON.parse(localStorage.getItem('localResponses') || '[]');
            const userResponse = localResponses.find(r => r.userId === currentUser.uid);
            
            if (userResponse && userResponse.status === 'confirmed') {
              setIsConfirmed(true);
            } else {
              navigate('/home', { 
                state: { 
                  message: 'You must be confirmed for the trip to view trip details. Please contact an admin.' 
                } 
              });
              return;
            }
          }
        } else if (isDevMode) {
          // Dev mode - allow access
          setIsConfirmed(true);
        } else {
          navigate('/home', { 
            state: { 
              message: 'You must be confirmed for the trip to view trip details. Please contact an admin.' 
            } 
          });
          return;
        }
      } catch (error) {
        console.error('Error checking confirmed status:', error);
        // Fallback: check localStorage
        const localResponses = JSON.parse(localStorage.getItem('localResponses') || '[]');
        const userResponse = localResponses.find(r => r.userId === currentUser?.uid);
        
        if (userResponse && userResponse.status === 'confirmed') {
          setIsConfirmed(true);
        } else {
          navigate('/home', { 
            state: { 
              message: 'You must be confirmed for the trip to view trip details. Please contact an admin.' 
            } 
          });
          return;
        }
      } finally {
        setLoading(false);
      }
    };

    checkConfirmedStatus();
  }, [currentUser, navigate]);

  // Initialize expanded days - current day expanded by default
  useEffect(() => {
    if (tripDetailsConfig?.itinerary?.days) {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to compare dates only
      
      const initialExpanded = {};
      let foundCurrentDay = false;
      
      tripDetailsConfig.itinerary.days.forEach((day, index) => {
        try {
          // Parse the date string (format: "January 16, 2026")
          const dayDate = new Date(day.date);
          dayDate.setHours(0, 0, 0, 0);
          
          // Check if this day is today or in the future
          const isTodayOrFuture = dayDate >= today;
          
          // If we haven't found the current day yet and this is today or the first future day, expand it
          if (!foundCurrentDay && isTodayOrFuture) {
            initialExpanded[index] = true;
            foundCurrentDay = true;
          } else {
            // Collapse all other days
            initialExpanded[index] = false;
          }
        } catch (e) {
          // If date parsing fails, default to collapsed
          initialExpanded[index] = false;
        }
      });
      
      // If no current/future day found, expand the first day
      if (!foundCurrentDay && tripDetailsConfig.itinerary.days.length > 0) {
        initialExpanded[0] = true;
      }
      
      setExpandedDays(initialExpanded);
    }
  }, []);

  // Try to extract photo from AirBnB URL (Open Graph preview image)
  useEffect(() => {
    const fetchAirbnbPhoto = async () => {
      setPhotoLoading(true);
      setPhotoError(false);
      
      if (tripDetailsConfig.lodging.photoUrl) {
        setLodgingPhoto(tripDetailsConfig.lodging.photoUrl);
        setPhotoLoading(false);
        return;
      }

      const airbnbUrl = tripDetailsConfig.lodging.airbnbUrl;
      
      // Try multiple methods to get the preview image (Open Graph image)
      const methods = [
        // Method 1: Try CORS proxy to fetch og:image meta tag
        async () => {
          try {
            const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(airbnbUrl)}`;
            const response = await fetch(proxyUrl);
            const data = await response.json();
            
            if (data.contents) {
              // Try multiple regex patterns for og:image
              const patterns = [
                /<meta\s+property=["']og:image["']\s+content=["']([^"']+)["']/i,
                /<meta\s+content=["']([^"']+)["']\s+property=["']og:image["']/i,
                /<meta\s+name=["']og:image["']\s+content=["']([^"']+)["']/i,
                /property=["']og:image["']\s+content=["']([^"']+)["']/i
              ];
              
              for (const pattern of patterns) {
                const match = data.contents.match(pattern);
                if (match && match[1]) {
                  return match[1];
                }
              }
              
              // Also try to find image URLs in JSON-LD structured data
              const jsonLdMatch = data.contents.match(/<script[^>]*type=["']application\/ld\+json["'][^>]*>(.*?)<\/script>/is);
              if (jsonLdMatch) {
                try {
                  const jsonLd = JSON.parse(jsonLdMatch[1]);
                  if (jsonLd.image) {
                    return Array.isArray(jsonLd.image) ? jsonLd.image[0] : jsonLd.image;
                  }
                } catch (e) {
                  // Ignore JSON parse errors
                }
              }
            }
          } catch (error) {
            console.log('CORS proxy method failed:', error);
          }
          return null;
        },
        
        // Method 2: Try alternative CORS proxy service
        async () => {
          try {
            const proxyUrl = `https://corsproxy.io/?${encodeURIComponent(airbnbUrl)}`;
            const response = await fetch(proxyUrl);
            const html = await response.text();
            
            const ogImageMatch = html.match(/<meta\s+property=["']og:image["']\s+content=["']([^"']+)["']/i);
            if (ogImageMatch && ogImageMatch[1]) {
              return ogImageMatch[1];
            }
          } catch (error) {
            console.log('Alternative CORS proxy failed:', error);
          }
          return null;
        }
      ];
      
      // Try each method until one succeeds
      for (const method of methods) {
        try {
          const imageUrl = await method();
          if (imageUrl) {
            setLodgingPhoto(imageUrl);
            // Don't set loading to false here - wait for image to actually load
            // The onLoad handler will set photoLoading to false
            return;
          }
        } catch (error) {
          console.log('Method failed:', error);
        }
      }
      
      // If all methods fail, set error state
      setPhotoLoading(false);
      setPhotoError(true);
      setLodgingPhoto(null);
    };

    fetchAirbnbPhoto();
  }, []);

  // Fetch restaurant photos from their websites (Open Graph images)
  useEffect(() => {
    const fetchRestaurantPhotos = async () => {
      if (!tripDetailsConfig?.itinerary?.days) return;

      const restaurantsToFetch = [];
      
      // Collect all unique restaurant URLs
      tripDetailsConfig.itinerary.days.forEach(day => {
        day.items.forEach(item => {
          if (item.restaurant && item.restaurant.url && !fetchedRestaurantUrls.current.has(item.restaurant.url)) {
            fetchedRestaurantUrls.current.add(item.restaurant.url);
            restaurantsToFetch.push({
              url: item.restaurant.url,
              name: item.restaurant.name,
              photoUrl: item.restaurant.photoUrl // Include manual photoUrl if provided
            });
          }
        });
      });

      // Fetch photos for each restaurant
      for (const restaurant of restaurantsToFetch) {
        // If manual photoUrl is provided, use it directly
        if (restaurant.photoUrl) {
          setRestaurantPhotos(prev => ({ ...prev, [restaurant.url]: restaurant.photoUrl }));
          setRestaurantPhotoLoading(prev => ({ ...prev, [restaurant.url]: false }));
          setRestaurantPhotoError(prev => ({ ...prev, [restaurant.url]: false }));
          continue;
        }

        setRestaurantPhotoLoading(prev => ({ ...prev, [restaurant.url]: true }));
        setRestaurantPhotoError(prev => ({ ...prev, [restaurant.url]: false }));

        // Helper function to extract photo URL from HTML content
        const extractPhotoUrl = (htmlContent) => {
          if (!htmlContent) return null;

          // Try multiple regex patterns for og:image
          const patterns = [
            /<meta\s+property=["']og:image["']\s+content=["']([^"']+)["']/i,
            /<meta\s+content=["']([^"']+)["']\s+property=["']og:image["']/i,
            /<meta\s+name=["']og:image["']\s+content=["']([^"']+)["']/i,
            /property=["']og:image["']\s+content=["']([^"']+)["']/i
          ];
          
          let photoUrl = null;
          for (const pattern of patterns) {
            const match = htmlContent.match(pattern);
            if (match && match[1]) {
              photoUrl = match[1];
              break;
            }
          }
          
          // Also try to find image URLs in JSON-LD structured data
          if (!photoUrl) {
            const jsonLdMatch = htmlContent.match(/<script[^>]*type=["']application\/ld\+json["'][^>]*>(.*?)<\/script>/is);
            if (jsonLdMatch) {
              try {
                const jsonLd = JSON.parse(jsonLdMatch[1]);
                if (jsonLd.image) {
                  photoUrl = Array.isArray(jsonLd.image) ? jsonLd.image[0] : jsonLd.image;
                }
              } catch (e) {
                // Ignore JSON parse errors
              }
            }
          }

          return photoUrl;
        };

        // Try multiple CORS proxy methods
        const methods = [
          // Method 1: allorigins.win
          async () => {
            try {
              const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(restaurant.url)}`;
              const response = await fetch(proxyUrl, { 
                method: 'GET',
                headers: {
                  'Accept': 'application/json',
                }
              });
              if (!response.ok) throw new Error('Response not OK');
              const data = await response.json();
              return data.contents ? extractPhotoUrl(data.contents) : null;
            } catch (error) {
              console.log(`allorigins.win failed for ${restaurant.name}:`, error);
              return null;
            }
          },
          // Method 2: corsproxy.io
          async () => {
            try {
              const proxyUrl = `https://corsproxy.io/?${encodeURIComponent(restaurant.url)}`;
              const response = await fetch(proxyUrl, {
                method: 'GET',
                headers: {
                  'Accept': 'text/html',
                }
              });
              if (!response.ok) throw new Error('Response not OK');
              const html = await response.text();
              return extractPhotoUrl(html);
            } catch (error) {
              console.log(`corsproxy.io failed for ${restaurant.name}:`, error);
              return null;
            }
          },
          // Method 3: cors-anywhere alternative (if available)
          async () => {
            try {
              const proxyUrl = `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(restaurant.url)}`;
              const response = await fetch(proxyUrl);
              if (!response.ok) throw new Error('Response not OK');
              const html = await response.text();
              return extractPhotoUrl(html);
            } catch (error) {
              console.log(`codetabs proxy failed for ${restaurant.name}:`, error);
              return null;
            }
          }
        ];

        // Try each method until one succeeds
        let photoUrl = null;
        for (const method of methods) {
          try {
            photoUrl = await method();
            if (photoUrl) break;
          } catch (error) {
            // Continue to next method
            continue;
          }
        }

        if (photoUrl) {
          setRestaurantPhotos(prev => ({ ...prev, [restaurant.url]: photoUrl }));
          setRestaurantPhotoLoading(prev => ({ ...prev, [restaurant.url]: false }));
        } else {
          // If all methods fail, set error state (will show fallback emoji)
          setRestaurantPhotoError(prev => ({ ...prev, [restaurant.url]: true }));
          setRestaurantPhotoLoading(prev => ({ ...prev, [restaurant.url]: false }));
        }
      }
    };

    fetchRestaurantPhotos();
  }, []);

  const copyToClipboard = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess({ ...copySuccess, [key]: true });
      setTimeout(() => {
        setCopySuccess({ ...copySuccess, [key]: false });
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopySuccess({ ...copySuccess, [key]: true });
        setTimeout(() => {
          setCopySuccess({ ...copySuccess, [key]: false });
        }, 2000);
      } catch (err) {
        console.error('Fallback copy failed:', err);
      }
      document.body.removeChild(textArea);
    }
  };

  const renderLodgingSection = () => {
    const { lodging } = tripDetailsConfig;
    
    return (
      <div className="question-card">
        <h2 className="question-title">🏠 {lodging.title}</h2>
        
        {/* AirBnB Link Preview Card */}
        <a
          href={lodging.airbnbUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'block',
            textDecoration: 'none',
            marginBottom: '20px',
            borderRadius: '12px',
            overflow: 'hidden',
            border: '1px solid var(--border-color)',
            background: 'var(--bg-primary)',
            transition: 'all 0.3s ease',
            boxShadow: '0 2px 8px var(--shadow)'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 12px var(--shadow-hover)';
            e.currentTarget.style.borderColor = '#FF5A5F';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px var(--shadow)';
            e.currentTarget.style.borderColor = 'var(--border-color)';
          }}
        >
          {/* Photo section with loading shimmer */}
          <div style={{
            width: '100%',
            height: '250px',
            overflow: 'hidden',
            background: 'var(--bg-secondary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative'
          }}>
            {photoLoading && (
              <div 
                className="shimmer"
                style={{
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  top: 0,
                  left: 0
                }}
              />
            )}
            {photoError && !photoLoading && (
              <div style={{
                fontSize: '4rem',
                opacity: 0.3
              }}>
                🏠
              </div>
            )}
            {lodgingPhoto && (
              <img 
                src={lodgingPhoto} 
                alt="AirBnB Listing" 
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                  opacity: photoLoading ? 0 : 1,
                  transition: 'opacity 0.3s ease'
                }}
                onError={() => {
                  setLodgingPhoto(null);
                  setPhotoError(true);
                  setPhotoLoading(false);
                }}
                onLoad={() => {
                  setPhotoLoading(false);
                  setPhotoError(false);
                }}
              />
            )}
          </div>
          <div style={{
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ flex: 1 }}>
              <div style={{
                marginBottom: '4px'
              }}>
                <span style={{
                  color: 'var(--text-primary)',
                  fontWeight: '600',
                  fontSize: '1rem'
                }}>
                  View on AirBnB
                </span>
              </div>
              <div style={{
                color: 'var(--text-tertiary)',
                fontSize: '0.85rem'
              }}>
                airbnb.com
              </div>
            </div>
            <div style={{
              color: '#FF5A5F',
              fontSize: '1.5rem',
              fontWeight: 'bold'
            }}>
              →
            </div>
          </div>
        </a>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '16px',
          background: 'var(--bg-secondary)',
          borderRadius: '12px',
          border: '1px solid var(--border-color)'
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ 
              fontSize: '0.9rem', 
              color: 'var(--text-tertiary)',
              marginBottom: '4px'
            }}>
              Address:
            </div>
            <div style={{ 
              fontSize: '1rem', 
              color: 'var(--text-primary)',
              fontWeight: '500'
            }}>
              {lodging.address}
            </div>
          </div>
          <button
            onClick={() => copyToClipboard(lodging.address, 'address')}
            style={{
              padding: '8px 16px',
              background: copySuccess.address ? '#c6f6d5' : 'var(--bg-primary)',
              border: `2px solid ${copySuccess.address ? '#9ae6b4' : 'var(--border-color)'}`,
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: '600',
              color: copySuccess.address ? '#2f855a' : 'var(--text-secondary)',
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap'
            }}
          >
            {copySuccess.address ? '✓ Copied!' : '📋 Copy'}
          </button>
        </div>

        {/* Door Code - Only visible to confirmed users */}
        {isConfirmed && lodging.doorCode && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '16px',
            background: 'linear-gradient(135deg, #fef5e7 0%, #f6e05e 100%)',
            borderRadius: '12px',
            border: '2px solid #f6e05e',
            marginTop: '15px'
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ 
                fontSize: '0.9rem', 
                color: '#d69e2e',
                marginBottom: '4px',
                fontWeight: '600'
              }}>
                🔑 Door Code:
              </div>
              <div style={{ 
                fontSize: '1.2rem', 
                color: '#b7791f',
                fontWeight: '700',
                fontFamily: 'monospace',
                letterSpacing: '2px'
              }}>
                {lodging.doorCode}
              </div>
            </div>
            <button
              onClick={() => copyToClipboard(lodging.doorCode, 'doorCode')}
              style={{
                padding: '8px 16px',
                background: copySuccess.doorCode ? '#c6f6d5' : 'white',
                border: `2px solid ${copySuccess.doorCode ? '#9ae6b4' : '#f6e05e'}`,
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '600',
                color: copySuccess.doorCode ? '#2f855a' : '#d69e2e',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap'
              }}
            >
              {copySuccess.doorCode ? '✓ Copied!' : '📋 Copy'}
            </button>
          </div>
        )}

        {/* Garage Code - Only visible to confirmed users */}
        {isConfirmed && lodging.garageCode && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '16px',
            background: 'linear-gradient(135deg, #fef5e7 0%, #f6e05e 100%)',
            borderRadius: '12px',
            border: '2px solid #f6e05e',
            marginTop: '15px'
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ 
                fontSize: '0.9rem', 
                color: '#d69e2e',
                marginBottom: '4px',
                fontWeight: '600'
              }}>
                🚗 Garage Code:
              </div>
              <div style={{ 
                fontSize: '1.2rem', 
                color: '#b7791f',
                fontWeight: '700',
                fontFamily: 'monospace',
                letterSpacing: '2px'
              }}>
                {lodging.garageCode}
              </div>
            </div>
            <button
              onClick={() => copyToClipboard(lodging.garageCode, 'garageCode')}
              style={{
                padding: '8px 16px',
                background: copySuccess.garageCode ? '#c6f6d5' : 'white',
                border: `2px solid ${copySuccess.garageCode ? '#9ae6b4' : '#f6e05e'}`,
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '600',
                color: copySuccess.garageCode ? '#2f855a' : '#d69e2e',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap'
              }}
            >
              {copySuccess.garageCode ? '✓ Copied!' : '📋 Copy'}
            </button>
          </div>
        )}

        {/* Lodging & Beds link */}
        {tripDetailsConfig?.lodgingBeds && (
          <div style={{ marginTop: '15px' }}>
            <button
              onClick={() => navigate('/trip-details/lodging-beds', { state: { from: '/trip-details' } })}
              className="btn btn-primary"
              style={{
                width: '100%',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                border: 'none'
              }}
            >
              🛏️ {tripDetailsConfig.lodgingBeds.title}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderWifiSection = () => {
    const { wifi } = tripDetailsConfig;
    
    return (
      <div className="question-card">
        <h2 className="question-title">📶 {wifi.title}</h2>
        
        {wifi.comingSoon ? (
          <div style={{
            padding: '20px',
            background: 'var(--bg-secondary)',
            borderRadius: '12px',
            border: '1px solid var(--border-color)',
            textAlign: 'center',
            color: 'var(--text-tertiary)'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '10px' }}>⏳</div>
            <div style={{ fontSize: '1rem', fontWeight: '500' }}>Coming Soon</div>
            <div style={{ fontSize: '0.9rem', marginTop: '8px' }}>
              WiFi information will be available closer to the trip date.
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '16px',
              background: 'var(--bg-secondary)',
              borderRadius: '12px',
              border: '1px solid var(--border-color)'
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ 
                  fontSize: '0.9rem', 
                  color: 'var(--text-tertiary)',
                  marginBottom: '4px'
                }}>
                  SSID:
                </div>
                <div style={{ 
                  fontSize: '1rem', 
                  color: 'var(--text-primary)',
                  fontWeight: '500',
                  fontFamily: 'monospace'
                }}>
                  {wifi.ssid || 'Not set'}
                </div>
              </div>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '16px',
              background: 'var(--bg-secondary)',
              borderRadius: '12px',
              border: '1px solid var(--border-color)'
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ 
                  fontSize: '0.9rem', 
                  color: 'var(--text-tertiary)',
                  marginBottom: '4px'
                }}>
                  Password:
                </div>
                <div style={{ 
                  fontSize: '1rem', 
                  color: 'var(--text-primary)',
                  fontWeight: '500',
                  fontFamily: 'monospace',
                  letterSpacing: '2px'
                }}>
                  {wifi.password ? '•'.repeat(wifi.password.length) : 'Not set'}
                </div>
              </div>
              {wifi.password && (
                <button
                  onClick={() => copyToClipboard(wifi.password, 'wifiPassword')}
                  style={{
                    padding: '8px 16px',
                    background: copySuccess.wifiPassword ? '#c6f6d5' : 'var(--bg-primary)',
                    border: `2px solid ${copySuccess.wifiPassword ? '#9ae6b4' : 'var(--border-color)'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: copySuccess.wifiPassword ? '#2f855a' : 'var(--text-secondary)',
                    transition: 'all 0.2s ease',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {copySuccess.wifiPassword ? '✓ Copied!' : '📋 Copy'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const toggleDay = (dayIndex) => {
    setExpandedDays(prev => {
      const isCurrentlyExpanded = prev[dayIndex] === true;
      
      // If clicking on an already expanded day, collapse it
      if (isCurrentlyExpanded) {
        return {
          ...prev,
          [dayIndex]: false
        };
      }
      
      // If clicking on a collapsed day, expand it and collapse all others
      const newExpandedDays = {};
      if (tripDetailsConfig?.itinerary?.days) {
        tripDetailsConfig.itinerary.days.forEach((_, index) => {
          newExpandedDays[index] = index === dayIndex;
        });
      }
      
      return newExpandedDays;
    });
  };

  const renderItinerarySection = () => {
    const { itinerary } = tripDetailsConfig;
    
    return (
      <div className="question-card">
        <h2 className="question-title">📅 {itinerary.title}</h2>
        
        {itinerary.comingSoon || !itinerary.days || itinerary.days.length === 0 ? (
          <div style={{
            padding: '20px',
            background: 'var(--bg-secondary)',
            borderRadius: '12px',
            border: '1px solid var(--border-color)',
            textAlign: 'center',
            color: 'var(--text-tertiary)'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '10px' }}>📋</div>
            <div style={{ fontSize: '1rem', fontWeight: '500' }}>More Info Coming Soon</div>
            <div style={{ fontSize: '0.9rem', marginTop: '8px' }}>
              The itinerary will be updated as we finalize plans for the trip.
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {itinerary.days.map((day, dayIndex) => {
              const isExpanded = expandedDays[dayIndex] === true; // Only expanded if explicitly true
              
              return (
                <div
                  key={dayIndex}
                  style={{
                    background: 'var(--bg-secondary)',
                    borderRadius: '12px',
                    border: '1px solid var(--border-color)',
                    overflow: 'hidden'
                  }}
                >
                  {/* Clickable header */}
                  <div
                    onClick={() => toggleDay(dayIndex)}
                    style={{
                      padding: '20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      userSelect: 'none',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--bg-primary)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        fontSize: '1.2rem', 
                        fontWeight: '700',
                        color: 'var(--text-primary)',
                        marginBottom: '4px'
                      }}>
                        {day.dayOfWeek}
                      </div>
                      <div style={{ 
                        fontSize: '0.95rem', 
                        color: 'var(--text-tertiary)'
                      }}>
                        {day.date}
                      </div>
                    </div>
                    <div style={{
                      fontSize: '1.5rem',
                      color: 'var(--text-tertiary)',
                      transition: 'transform 0.3s ease',
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                    }}>
                      ▼
                    </div>
                  </div>
                  
                  {/* Collapsible content */}
                  {isExpanded && (
                    <div style={{ 
                      padding: '0 20px 20px 20px'
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: '12px',
                        position: 'relative',
                        paddingLeft: '24px'
                      }}>
                  {/* Timeline line */}
                  <div style={{
                    position: 'absolute',
                    left: '8px',
                    top: '0',
                    bottom: '0',
                    width: '2px',
                    background: 'linear-gradient(to bottom, #10b981, #059669)',
                    borderRadius: '1px'
                  }} />
                  
                  {day.items.map((item, itemIndex) => (
                    <div
                      key={itemIndex}
                      style={{
                        position: 'relative',
                        paddingBottom: itemIndex < day.items.length - 1 ? '0' : '0'
                      }}
                    >
                      {/* Timeline dot */}
                      <div style={{
                        position: 'absolute',
                        left: '-20px',
                        top: '6px',
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        background: '#10b981',
                        border: '2px solid var(--bg-secondary)',
                        zIndex: 1
                      }} />
                      
                      <div style={{
                        padding: '12px',
                        background: 'var(--bg-primary)',
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)'
                      }}>
                        <div style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '6px'
                        }}>
                          <div style={{ 
                            fontSize: '0.9rem', 
                            fontWeight: '600',
                            color: '#10b981',
                            fontFamily: 'monospace'
                          }}>
                            {item.time}
                          </div>
                        </div>
                        <div style={{ 
                          fontSize: '1rem', 
                          fontWeight: '600',
                          color: 'var(--text-primary)',
                          marginBottom: '4px'
                        }}>
                          {item.activity}
                        </div>
                        {item.restaurant && (
                          <div style={{ marginTop: '12px' }}>
                            {/* Restaurant Link Preview Card with Photo - Matching Airbnb style */}
                            <a
                              href={item.restaurant.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: 'block',
                                textDecoration: 'none',
                                marginBottom: '20px',
                                borderRadius: '12px',
                                overflow: 'hidden',
                                border: '1px solid var(--border-color)',
                                background: 'var(--bg-primary)',
                                transition: 'all 0.3s ease',
                                boxShadow: '0 2px 8px var(--shadow)'
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px var(--shadow-hover)';
                                e.currentTarget.style.borderColor = '#FF5A5F';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 8px var(--shadow)';
                                e.currentTarget.style.borderColor = 'var(--border-color)';
                              }}
                            >
                              {/* Photo section with loading shimmer */}
                              <div style={{
                                width: '100%',
                                height: '250px',
                                overflow: 'hidden',
                                background: 'var(--bg-secondary)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative'
                              }}>
                                {(restaurantPhotoLoading[item.restaurant.url] && !item.restaurant.photoUrl) && (
                                  <div 
                                    className="shimmer"
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      position: 'absolute',
                                      top: 0,
                                      left: 0
                                    }}
                                  />
                                )}
                                {((restaurantPhotoError[item.restaurant.url] && !restaurantPhotoLoading[item.restaurant.url] && !item.restaurant.photoUrl) || 
                                  (!restaurantPhotos[item.restaurant.url] && !item.restaurant.photoUrl)) && (
                                  <div style={{
                                    fontSize: '4rem',
                                    opacity: 0.3
                                  }}>
                                    🍽️
                                  </div>
                                )}
                                {(restaurantPhotos[item.restaurant.url] || item.restaurant.photoUrl) && (
                                  <img 
                                    src={restaurantPhotos[item.restaurant.url] || item.restaurant.photoUrl} 
                                    alt={item.restaurant.name} 
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                      display: 'block',
                                      opacity: restaurantPhotoLoading[item.restaurant.url] ? 0 : 1,
                                      transition: 'opacity 0.3s ease'
                                    }}
                                    onError={() => {
                                      setRestaurantPhotos(prev => {
                                        const updated = { ...prev };
                                        delete updated[item.restaurant.url];
                                        return updated;
                                      });
                                      setRestaurantPhotoError(prev => ({ ...prev, [item.restaurant.url]: true }));
                                      setRestaurantPhotoLoading(prev => ({ ...prev, [item.restaurant.url]: false }));
                                    }}
                                    onLoad={() => {
                                      setRestaurantPhotoLoading(prev => ({ ...prev, [item.restaurant.url]: false }));
                                      setRestaurantPhotoError(prev => ({ ...prev, [item.restaurant.url]: false }));
                                    }}
                                  />
                                )}
                              </div>
                              <div style={{
                                padding: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                              }}>
                                <div style={{ flex: 1 }}>
                                  <div style={{
                                    marginBottom: '4px'
                                  }}>
                                    <span style={{
                                      color: 'var(--text-primary)',
                                      fontWeight: '600',
                                      fontSize: '1rem'
                                    }}>
                                      {item.restaurant.name}
                                    </span>
                                  </div>
                                  <div style={{
                                    color: 'var(--text-tertiary)',
                                    fontSize: '0.85rem',
                                    marginBottom: '4px'
                                  }}>
                                    📍 {item.restaurant.address}
                                  </div>
                                  <div style={{
                                    color: 'var(--text-tertiary)',
                                    fontSize: '0.85rem'
                                  }}>
                                    {new URL(item.restaurant.url).hostname.replace('www.', '')}
                                  </div>
                                </div>
                                <div style={{
                                  color: '#FF5A5F',
                                  fontSize: '1.5rem',
                                  fontWeight: 'bold'
                                }}>
                                  →
                                </div>
                              </div>
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="container">
        <div className="card">
          <div className="loading">Loading...</div>
        </div>
      </div>
    );
  }

  if (!isConfirmed) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="container">
      <div className="card">
        <div className="card-header">
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-start', 
            marginBottom: '20px' 
          }}>
            <button
              onClick={() => navigate('/home')}
              className="btn btn-secondary"
              style={{ 
                width: 'auto', 
                padding: '8px 16px', 
                fontSize: '0.9rem',
                borderRadius: '8px'
              }}
            >
              ← Back
            </button>
          </div>
          <div style={{ fontSize: '4rem', marginBottom: '20px' }}>🏔️</div>
          <h1 className="card-title">Trip Details</h1>
          <p className="card-subtitle">
            All the information you need for your Whistler adventure!
          </p>
        </div>

        {renderLodgingSection()}
        {renderWifiSection()}
        {renderItinerarySection()}
      </div>
    </div>
  );
}

export default TripDetails;

